import { InstallmentsConfig } from 'services/types/installmensts.config';

// Mirrors apps/mobile/src/lib/fees.ts so web and mobile show identical numbers.
// Keep the two in sync if either formula changes.

export type InstallmentMode = 'FROM_PAYER' | 'FROM_RECEIVER';

export type ReceivableRow = {
  installments: number;
  feePercent: number;
  installmentValue: number;
  totalValue: number;
  netValue: number;
};

export type CardRange = {
  low: number;
  lowInstallments: number;
  high: number;
  highInstallments: number;
};

const DEFAULT_MIN_INSTALLMENT = 5;

/** Flat fee for Pix/boleto. PERCENTUAL clamped by minFixed/maxFixed; FIXED = config amount. */
export function calculateFlatFee(config: InstallmentsConfig, saleValue: number): number {
  const amount = parseFloat(config.amount) || 0;
  if (config.type === 'FIXED') return amount;
  const min = parseFloat(config.minFixed ?? '0') || 0;
  const max = parseFloat(config.maxFixed ?? '0') || 0;
  let computed = saleValue * (amount / 100);
  if (max > 0) computed = Math.min(computed, max);
  if (min > 0) computed = Math.max(computed, min);
  return computed;
}

export function flatNet(config: InstallmentsConfig, amount: number): { fee: number; net: number } {
  const fee = calculateFlatFee(config, amount);
  return { fee, net: amount - fee };
}

/** Per-installment rows for a card method, capped by min(merchant max, config max). */
export function calculateInstallments(
  config: InstallmentsConfig,
  amount: number,
  mode: InstallmentMode,
  maxInstallments: number,
  minInstallmentValue: number = DEFAULT_MIN_INSTALLMENT,
): ReceivableRow[] {
  // Web caps here internally. NOTE: mobile's mirror (apps/mobile/src/lib/fees.ts:calculateInstallments) does NOT cap by config.maxInstallments — its callers must pre-cap. Keep both cap paths in sync.
  const cap = Math.min(maxInstallments || 0, config.maxInstallments || 0);
  if (!amount || amount <= 0 || !cap) return [];
  const rows: ReceivableRow[] = [];
  for (let n = 1; n <= cap; n++) {
    const fee = (config.installments?.[String(n)]?.fee ?? 0) / 100;
    let installmentValue: number;
    let totalValue: number;
    let netValue: number;
    if (mode === 'FROM_PAYER') {
      totalValue = fee < 1 ? amount / (1 - fee) : amount;
      installmentValue = totalValue / n;
      netValue = amount;
    } else {
      totalValue = amount;
      installmentValue = totalValue / n;
      netValue = amount * (1 - fee);
    }
    if (installmentValue < minInstallmentValue) continue;
    rows.push({ installments: n, feePercent: fee * 100, installmentValue, totalValue, netValue });
  }
  return rows;
}

/** Range of merchant receivables across installments when assuming the fee (FROM_RECEIVER). */
export function cardRange(rows: ReceivableRow[]): CardRange | null {
  if (rows.length === 0) return null;
  const first = rows[0];
  const last = rows[rows.length - 1];
  return {
    high: first.netValue,
    highInstallments: first.installments,
    low: last.netValue,
    lowInstallments: last.installments,
  };
}
